import { PRODUCT_NAME } from "@amtana/i18n/catalog";

import type { ComparisonWords } from "./comparison-page.ts";
import { CHECKED_ON, SOURCES } from "./comparison-sources.ts";

// /compare/instagram-email-extractors, in English. It names approaches only (ADR-034 point 4), and
// the product's row claims only what it reads: followers and following (D1151).
const USE_CASE = "/use-cases/competitor-followers";

export const INSTAGRAM_EMAIL_EXTRACTORS_WORDS_EN: ComparisonWords = {
  linkLabel: "Email extractors",
  title: "Instagram email extractors compared",
  description:
    "Instagram email extractors compared by approach: browser extensions, list brokers and generic scrapers, where each is strong and what to check.",
  keyphrase: "instagram email extractors",
  heroTitle: "Instagram email extractors: four approaches compared",
  lede: "Pick by the job. Each approach suits a different one, so here is where each is strong and what to check first.",
  tableLabel: "Four approaches to finding contacts on Instagram",
  rows: [
    {
      approach: "Browser extension",
      strong: {
        text: "It runs in your own browser and reads the pages you open.",
        source: { label: "Chrome for Developers", href: SOURCES.contentScripts },
        checkedOn: CHECKED_ON,
      },
      check: {
        text: "Which site data it asks to read, and on how many sites.",
        source: { label: "Chrome Web Store Help", href: SOURCES.extensionPermissions },
        checkedOn: CHECKED_ON,
      },
      useCase: USE_CASE,
    },
    {
      approach: "List broker",
      strong: {
        text: "You start from a list someone else already gathered.",
        source: { label: "ICO, the UK regulator", href: SOURCES.boughtInLists },
        checkedOn: CHECKED_ON,
      },
      check: {
        text: "Whether the people on it agreed to marketing from you, by that channel.",
        source: { label: "ICO, the UK regulator", href: SOURCES.boughtInLists },
        checkedOn: CHECKED_ON,
      },
      useCase: USE_CASE,
    },
    {
      approach: "Generic scraper",
      strong: {
        text: "A program that browses pages and collects data from them, so you choose the pages.",
        source: { label: "MDN Web Docs", href: SOURCES.crawler },
        checkedOn: CHECKED_ON,
      },
      check: {
        text: "Who fixes it when a page changes, since code tied to a page's structure can fail.",
        source: { label: "Playwright docs", href: SOURCES.markupChanges },
        checkedOn: CHECKED_ON,
      },
      useCase: USE_CASE,
    },
  ],
  own: {
    approach: "Reading one audience",
    byline: PRODUCT_NAME,
    strong: "The followers or the following of an account you choose.",
    check:
      "Instagram followers and following only. Private accounts are not read, and not every public profile shows a contact.",
    useCase: USE_CASE,
  },
  questions: [
    {
      question: "Which approach suits a short list?",
      answer:
        "One that starts from an audience your buyers already follow. That is our view, not a measurement.",
    },
    {
      question: `What does ${PRODUCT_NAME} read?`,
      answer: "An Instagram account's followers or its following, and only public profiles.",
    },
    {
      question: "What does a credit buy?",
      answer: "1 credit reads 1 public profile, with or without a contact. Private accounts are not read.",
    },
    {
      question: "Does a public contact mean I may send marketing to it?",
      answer: "No. A public contact is not consent, so check the marketing rules that apply to you first.",
    },
  ],
  ctaTitle: "Try it on one account",
  ctaDescription: "Your first analysis runs on free credits.",
};
